import { useCallback, useEffect } from "react";
import { toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useLineMachines } from "@/features/lines/hooks";
import { useLineCanvas } from "@/features/lines/canvas/hooks";
import { toScenarioStructure } from "@/types/sim-sandbox.types";
import { buildDraftFromSnapshot } from "../sandbox-draft.helpers";
import { useSandboxDraft } from "./useSandboxDraft";
import { useSaveScenarioStructure, useScenarioStructure, useOrgMachines } from "./useSandboxApi";

// Orquestra o sandbox: hidrata o rascunho da linha (uma vez por cenário),
// expõe estado/ações do store e o "salvar estrutura" (PATCH). Mantém a página ≤150 LOC.
export function useSandboxController(scenarioId: string | undefined) {
  const scenarioQuery = useScenarioStructure(scenarioId);
  const { data: scenario, isError: scenarioIsError, error: scenarioError } = scenarioQuery;
  const lineId = scenario?.lineId ?? "";
  const { data: machines = [], isLoading: machinesLoading } = useLineMachines(lineId || "");
  const { data: canvas } = useLineCanvas(lineId, !!lineId);
  const save = useSaveScenarioStructure();

  // Reabrir cenário salvo: nomes do acervo só quando o snapshot referencia máquinas de
  // origem "acervo" — cenários puros de linha não pagam a consulta org-wide. Limitação conhecida:
  // com >100 máquinas na org, uma máquina de acervo fora da 1ª página resolve p/ nome neutro.
  const hasSnap = !!scenario?.structureSnap;
  const hasAcervo = !!scenario?.structureSnap?.machines.some((m) => m.origin === "acervo");
  const { data: orgMachines } = useOrgMachines({ pageSize: 100 }, hasSnap && hasAcervo);

  const draft = useSandboxDraft();

  useEffect(() => {
    if (!scenario || !scenarioId) return;
    if (draft.scenarioId === scenarioId) return; // já hidratado neste cenário
    const productId = scenario.structureSnap?.productId ?? scenario.productId ?? null;

    // Cenário já salvo: rehidrata do snapshot (edits preservados).
    if (scenario.structureSnap) {
      if (lineId && machinesLoading) return; // aguarda nomes das máquinas da linha
      if (hasAcervo && !orgMachines) return; // aguarda nomes do acervo (só quando há acervo)
      const names = new Map<string, { code: string; name: string }>();
      for (const m of machines) names.set(m.id, { code: m.code, name: m.name });
      for (const m of orgMachines?.items ?? []) names.set(m.id, { code: m.code, name: m.name });
      draft.hydrateFromSnapshot(
        buildDraftFromSnapshot(scenario.structureSnap, {
          scenarioId,
          baseUpdatedAt: scenario.updatedAt,
          names,
        })
      );
      return;
    }

    if (lineId) {
      if (!canvas) return; // aguarda o layout da linha
      if (machinesLoading) return; // canvas pode resolver antes — sem isto, hidrataria com []
      draft.hydrate({
        scenarioId,
        baseLineId: lineId,
        baseUpdatedAt: scenario.updatedAt,
        productId,
        machines,
        nodes: canvas.nodes,
        edges: canvas.edges,
      });
    } else {
      // Cenário "linha nova": abre com canvas vazio (START/END).
      draft.hydrateNew({ scenarioId, productId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hidratação única por cenário
  }, [scenario, scenarioId, lineId, canvas, machines.length, machinesLoading, orgMachines]);

  // Descarta as edições do rascunho: re-hidrata da LINHA real (ou limpa, p/ linha nova).
  const restore = useCallback(() => {
    if (!scenarioId) return;
    const productId = scenario?.structureSnap?.productId ?? scenario?.productId ?? null;
    if (lineId && canvas && !machinesLoading) {
      draft.hydrate({
        scenarioId,
        baseLineId: lineId,
        baseUpdatedAt: scenario?.updatedAt ?? null,
        productId,
        machines,
        nodes: canvas.nodes,
        edges: canvas.edges,
      });
    } else {
      draft.hydrateNew({ scenarioId, productId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ação manual; usa dados já carregados
  }, [scenarioId, lineId, canvas, machinesLoading, machines, scenario]);

  const saveStructure = useCallback(async () => {
    if (!scenarioId) return;
    const structure = toScenarioStructure(useSandboxDraft.getState());
    try {
      const res = await save.mutateAsync({
        scenarioId,
        structure,
        baseUpdatedAt: useSandboxDraft.getState().baseUpdatedAt,
      });
      useSandboxDraft.getState().markSaved(res.updatedAt);
      if (res.concurrentEdit) {
        toast.warning("Editado em outra sessão", {
          description: "Sua versão foi salva por cima. Rodadas anteriores foram preservadas.",
        });
      }
    } catch (err) {
      toast.error("Não foi possível salvar o cenário.", {
        description: getFriendlyErrorMessage(err),
      });
      throw new Error("save-failed");
    }
  }, [save, scenarioId]);

  return {
    scenario,
    ready: !!scenario && draft.scenarioId === scenarioId,
    lineDrift: scenario?.lineDrift ?? false,
    hasBaseLine: !!lineId,
    saveStructure,
    restore,
    isSaving: save.isPending,
    // `useScenarioStructure` falhando não pode deixar o consumidor preso em
    // "Carregando cenário…" para sempre; expõe o erro para a página tratar (QueryErrorState).
    isError: scenarioIsError,
    error: scenarioError,
    refetchScenario: scenarioQuery.refetch,
  };
}
